import { type RefObject, useLayoutEffect, useRef, useState } from 'react';
import { PREVIEW_MAX_ZOOM, PREVIEW_MIN_ZOOM, PREVIEW_ZOOM_STEP } from './constants';
import { useDragToPan } from './useDragToPan';

type Point = { x: number; y: number };

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

/**
 * Zoom for the image preview that keeps the point in the middle of the view where it is, and lets
 * the reader drag the zoomed image around (the scrollbars are hidden).
 */
export function useImagePreviewZoom(scrollRef: RefObject<HTMLElement | null>, imageRef: RefObject<HTMLElement | null>) {
  const [zoom, setZoom] = useState(1);
  const { isPanning, panHandlers } = useDragToPan(scrollRef);
  // Where the view's centre sat on the image, as a fraction of its size, before the zoom changed.
  const anchorRef = useRef<Point | null>(null);

  const zoomTo = (next: number) => {
    const scroller = scrollRef.current;
    const image = imageRef.current;
    if (scroller && image) {
      const view = scroller.getBoundingClientRect();
      const rect = image.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        anchorRef.current = {
          x: clamp((view.left + view.width / 2 - rect.left) / rect.width, 0, 1),
          y: clamp((view.top + view.height / 2 - rect.top) / rect.height, 0, 1),
        };
      }
    }
    setZoom(next);
  };

  // Scroll so the anchored point is back in the middle of the view at the new size.
  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    const scroller = scrollRef.current;
    const image = imageRef.current;
    anchorRef.current = null;
    if (!anchor || !scroller || !image) return;

    const view = scroller.getBoundingClientRect();
    // The bounding box of a rotated image is its turned footprint, which is what's on screen.
    const rect = image.getBoundingClientRect();
    scroller.scrollLeft += rect.left + anchor.x * rect.width - (view.left + view.width / 2);
    scroller.scrollTop += rect.top + anchor.y * rect.height - (view.top + view.height / 2);
  }, [zoom, scrollRef, imageRef]);

  return {
    zoom,
    zoomIn: () => zoomTo(Math.min(zoom + PREVIEW_ZOOM_STEP, PREVIEW_MAX_ZOOM)),
    zoomOut: () => zoomTo(Math.max(zoom - PREVIEW_ZOOM_STEP, PREVIEW_MIN_ZOOM)),
    canZoomIn: zoom < PREVIEW_MAX_ZOOM,
    canZoomOut: zoom > PREVIEW_MIN_ZOOM,
    isPanning,
    panHandlers,
  };
}
